import * as React from 'react';
import strings from '../../../../../strings/strings.json';
import useNeveForm from 'components/forms/NeveForm';
import useUploadRequest from 'hooks/useUploadRequest';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { FlexColSm, Headline3 } from 'components/Elements';
import { ErrorMessage } from 'components/forms/FormComponents';
import useModifyRequest from 'hooks/useModifyRequest';
import { logError } from 'lib/debug-helpers';
import { APIError } from 'lib/_api-helpers';
import { Activity, ActivityType } from 'models/Activity';
import { FileDocument, FileDocumentType } from 'models/FileDocument';
import { createGlobalErrorFromAPIError, createFieldErrorFromAPIError } from 'shared/error-messages';
import useFormBlocker from 'components/forms/useFormBlocker';
import { AudioCard } from '../../FileCard';
import { AudioSelectionComponent } from '../../FileSelectionComponent';
import { LearningResourcesFormModal } from '../../LearningResourcesFormModal';
import ResourcesList from '../../ResourcesList';
import GeneratedContentSection from './shared/GeneratedContentSection';
import DeleteActivityAlert from './shared/DeleteActivityAlert';
import { Course } from 'models/Course';
import { Resource } from 'models/Resources';
import { EditTaskFormProps, ModuleOnlyHasOneTask, FileCardComponentData } from './shared/SharedEditTaskComponents';
import {
    ActivityFormState,
    ActivityDetailsInput,
    ResourcesInput,
    EditAudioTaskInput,
} from '../shared/SharedActivityFormComponents';
import BaseDetailsForm from '../shared/DetailsForm';
import { EditActivityFormCard } from '../shared/EditActivityFormCard';
import { UpdateAudioTaskFields, UpdateFileFields } from 'models/inputModels/CreateTaskRequest';
import { useFieldArray } from 'react-hook-form';

export default function EditAudioTaskForm({
    task: taskProp,
    returnToCourseStructure,
}: EditTaskFormProps): JSX.Element {
    const course = React.useContext(BuildCourseContext).course!;
    const { setCourseState, editMode, refetchCourse, setEditActivityId } = React.useContext(BuildCourseContext);

    const [task, setTask] = React.useState<Activity>(taskProp);
    const [state, setState] = React.useState<ActivityFormState>('clean');
    const [audioRequired, setAudioRequired] = React.useState<boolean>(false);
    const [resourcesModalOpen, setResourcesModalOpen] = React.useState(false);
    const [deleteTaskAlertOpen, setDeleteAlertOpen] = React.useState(false);
    const [globalErrorMessage, setGlobalErrorMessage] = React.useState<string>('');
    
    const [localAudioToBeUploaded, setLocalAudioToBeUploaded] = React.useState<File | null | undefined>(null);
    const [providerAudioToBeUploaded, setProviderAudioToBeUploaded] = React.useState<FileDocument>();
    const [audioCardData, setAudioCardComponentData] = React.useState<FileCardComponentData | null>(
        taskProp.audioDocument
            ? {
                  fileName: taskProp.audioDocument.filename,
                  fileDate: taskProp.audioDocument.dateUpdated,
                  filePath: taskProp.audioDocument.s3Filepath,
                  fileSource: 'provider',
              }
            : null,
    );

    useFormBlocker(state === 'dirty', strings.courseEditorPage.unsavedChangesPrompt);

    const selectAudioButtonRef = React.useRef<HTMLButtonElement>(null);
    const addLinkButtonRef = React.useRef<HTMLButtonElement>(null);
    const removeFileButtonRef = React.useRef<HTMLButtonElement>(null);

    const { upload, uploading: uploadingAudio } = useUploadRequest();
    const { modifyData: updateAudioTask, loading: updatingTask } = useModifyRequest<UpdateAudioTaskFields, boolean>(
        `activities/task/audio/${task.id}`,
        'PUT',
    );

    React.useEffect(() => {
        return () => {
            setEditActivityId('');
        };
    }, []); //eslint-disable-line

    React.useEffect(() => {
        if (taskProp) {
            setTask(taskProp);
            setGlobalErrorMessage('');
            setAudioRequired(false);
            setLocalAudioToBeUploaded(undefined);
            setProviderAudioToBeUploaded(undefined);
        }
    }, [taskProp]);

    React.useEffect(() => {
        let data: FileCardComponentData | null;

        if (localAudioToBeUploaded) {
            data = {
                fileSource: 'local',
                fileName: localAudioToBeUploaded.name,
                fileDate: new Date(),
                filePath: URL.createObjectURL(localAudioToBeUploaded),
            };
        } else if (providerAudioToBeUploaded) {
            data = {
                fileSource: 'provider',
                fileName: providerAudioToBeUploaded.filename,
                fileDate: providerAudioToBeUploaded.dateUpdated,
                filePath: providerAudioToBeUploaded.s3Filepath,
            };
        } else if (task.audioDocument) {
            data = {
                fileSource: 'provider',
                fileName: task.audioDocument.filename,
                fileDate: task.audioDocument.dateUpdated,
                filePath: task.audioDocument.s3Filepath,
            };
        } else {
            data = null;
        }

        if (localAudioToBeUploaded || providerAudioToBeUploaded) {
            setState('dirty');
        }

        setAudioCardComponentData(data);
    }, [task, localAudioToBeUploaded, providerAudioToBeUploaded]);

    React.useEffect(() => {
        setAudioRequired(!audioCardData);
    }, [audioCardData]);

    const {
        register,
        handleSubmit,
        control,
        setError,
        watch,
        formState: { errors },
        reset,
    } = useNeveForm<EditAudioTaskInput>({
        title: task.title ?? ' ',
        description: task.description ?? '',
        durationInMins: task.durationInMins ?? 1,
        resources: task.resources.map((x) => ({ url: x.url, title: x.title })),
    });

    const {
        fields: resourceFields,
        append: appendResource,
        remove: removeResource,
    } = useFieldArray({
        control,
        name: 'resources',
    });

    const title = watch('title');
    const description = watch('description');
    const durationInMins = watch('durationInMins');
    const resources = watch('resources');

    React.useEffect(() => {
        const titleChanged = title !== taskProp.title;
        const descriptionChanged = description !== taskProp.description;
        const durationInMinsChanged = durationInMins != taskProp.durationInMins; // eslint-disable-line eqeqeq
        const audioChanged = audioCardData?.fileName !== taskProp.audioDocument?.filename;

        const resourcesChanged = !(
            resources.length === taskProp.resources.length &&
            resources.every((v, i) => v.title === taskProp.resources[i].title && v.url === taskProp.resources[i].url)
        );

        const changed =
            titleChanged || descriptionChanged || durationInMinsChanged || resourcesChanged || audioChanged;
        if (changed) {
            setState('dirty');
        }
    }, [title, description, durationInMins, resources, audioCardData]); // eslint-disable-line

    const addResource = (newResource: ResourcesInput) => {
        appendResource(newResource);
        addLinkButtonRef.current?.focus();
    };
    const deleteResource = (index: number) => {
        removeResource(index);
        addLinkButtonRef.current?.focus();
    };

    async function uploadAudio(): Promise<UpdateFileFields | null> {
        if (localAudioToBeUploaded) {
            const { errors, value } = await upload(localAudioToBeUploaded);

            if (errors && errors.length > 0) {
                logError(errors);
                updateErrors(errors);
                throw new Error('Audio upload failed');
            }
            if (!value) {
                logError('Get presigned URL failed');
                updateErrors([{ field: 'global', code: 'get presigned URL failed' }]);
                throw new Error('Audio upload failed');
            }
            return {
                type: 'new',
                filePath: value.s3Key,
                fileName: value.filename,
            };
        } else if (providerAudioToBeUploaded) {
            return {
                type: 'existing',
                id: providerAudioToBeUploaded.id,
                fileName: providerAudioToBeUploaded.filename,
                filePath: providerAudioToBeUploaded.s3Filepath,
            };
        } else if (task.audioDocument) {
            return {
                type: 'existing',
                id: task.audioDocument.id,
                fileName: task.audioDocument.filename,
                filePath: task.audioDocument.s3Filepath,
            };
        } else {
            return null;
        }
    }

    async function saveTask(formData: EditAudioTaskInput) {
        if (!audioCardData) {
            selectAudioButtonRef.current?.focus();
            return;
        }
        if (state !== 'dirty') {
            setState('saved');
            return;
        }

        let updateAudioFields: UpdateFileFields | null = null;
        try {
            updateAudioFields = await uploadAudio();
        } catch (error: any) {
            logError(error);
            return;
        }
        if (!updateAudioFields) return;

        if (!editMode) {
            const request: UpdateAudioTaskFields = {
                title: formData.title,
                description: formData.description,
                durationInMins: formData.durationInMins ?? 1,
                resources: resources,
                audio: updateAudioFields,
            };
            const { errors } = await updateAudioTask(request);
            if (errors) {
                logError(errors);
                updateErrors(errors);
                return;
            }
            reset(formData);
            await refetchCourse();
            setState('saved');
            return;
        }

        setCourseState((prev) => {
            const copy = structuredClone(prev) as Course;
            const module = copy.modules.find((m) => m.id === task.moduleId);
            if (!module) return prev;
            const taskToUpdate = module.activities.find((a) => a.id === task.id);
            if (!taskToUpdate) return prev;

            taskToUpdate.title = formData.title;
            taskToUpdate.description = formData.description;
            taskToUpdate.durationInMins = formData.durationInMins ?? null;
            taskToUpdate.resources = resources as Resource[];
            taskToUpdate.content = updateAudioFields.type === 'new' ? '' : taskToUpdate.content; //transcript to be re-generated
            taskToUpdate.audioDocument = {
                id: updateAudioFields.type === 'new' ? '' : updateAudioFields.id,
                filename: updateAudioFields.fileName,
                s3Filepath: updateAudioFields.filePath,
                documentType: FileDocumentType.AUDIO,
                dateUpdated: new Date(),
            };
            return copy;
        });

        reset(formData);
        setState('saved');
    }

    function updateErrors(apiErrors: APIError[]): void {
        apiErrors.forEach((apiError) => {
            const { field, code } = apiError;
            if (field === 'global') {
                return setGlobalErrorMessage(createGlobalErrorFromAPIError(code));
            }
            setError(field as keyof ActivityDetailsInput, createFieldErrorFromAPIError(field, code));
        });
    }

    const removeAudio = () => {
        setLocalAudioToBeUploaded(undefined);
        setProviderAudioToBeUploaded(undefined);
        setAudioCardComponentData(null);
        setTask({ ...task, content: '', audioDocument: null });
        setState('dirty');
        selectAudioButtonRef.current?.focus();
    };

    const moduleOnlyHasOneTask = ModuleOnlyHasOneTask(course, task.moduleId);

    const DetailsAndLinks: JSX.Element = (
        <>
            <BaseDetailsForm register={register} errors={errors} autoFocus />
            <ResourcesList
                resources={resourceFields}
                toggleModalOpen={() => setResourcesModalOpen(!resourcesModalOpen)}
                deleteResource={deleteResource}
                disabled={uploadingAudio || updatingTask}
                addLinkButtonRef={addLinkButtonRef}
            />
        </>
    );

    const AudioAndTranscript: JSX.Element = (
        <FlexColSm>
            <Headline3>{strings.selectFileForm.addAudioTitle}</Headline3>
            {audioCardData ? (
                <AudioCard
                    fileName={audioCardData.fileName}
                    removeFile={removeAudio}
                    disabled={uploadingAudio || updatingTask}
                    filePath={audioCardData.filePath}
                    source={audioCardData.fileSource}
                    removeFileButtonRef={removeFileButtonRef}
                />
            ) : (
                <AudioSelectionComponent
                    setFileFromLocal={(newFile) => setLocalAudioToBeUploaded(newFile)}
                    setFileFromProvider={setProviderAudioToBeUploaded}
                    setFileFromLocalMetaData={() => {}}
                    disableButton={uploadingAudio || updatingTask}
                    selectFileButtonRef={selectAudioButtonRef}
                />
            )}
            {audioRequired && <ErrorMessage>{strings.activityUpdatePage.audioRequired}</ErrorMessage>}
            {!editMode && audioCardData && task.generateContentStatus && <GeneratedContentSection task={task} />}
        </FlexColSm>
    );

    return (
        <>
            <EditActivityFormCard
                title={strings.activityDetailsForm.instructionEdit}
                onSubmit={handleSubmit(saveTask)}
                pageOne={DetailsAndLinks}
                pageTwo={AudioAndTranscript}
                error={globalErrorMessage}
                showLastActivityInModuleWarning={editMode && moduleOnlyHasOneTask}
                showSavedSuccessfully={state === 'saved' && !editMode}
                showSavedSuccessfullyEditMode={state === 'saved' && editMode}
                setDeleteAlertOpen={() => setDeleteAlertOpen((prev) => !prev)}
                disableDeleteTaskButton={uploadingAudio || updatingTask || (editMode && moduleOnlyHasOneTask)}
                disableSubmitButton={uploadingAudio || updatingTask || (editMode && state === 'clean')}
                submitting={uploadingAudio || updatingTask}
                activityType={ActivityType.TASK}
            />
            {resourcesModalOpen && (
                <LearningResourcesFormModal
                    closeModal={() => setResourcesModalOpen(false)}
                    addResource={addResource}
                />
            )}
            {deleteTaskAlertOpen && (
                <DeleteActivityAlert
                    taskId={task.id}
                    moduleId={module.id}
                    returnToCourseStructure={returnToCourseStructure}
                    setAlertOpen={setDeleteAlertOpen}
                />
            )}
        </>
    );
}
